import { Children, forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from './internal/utils'

/**
 * antd Carousel. Exposes `goTo`/`next`/`prev` on the ref, honors `autoplay`,
 * `dots` and `arrows`, and pauses autoplay while hovered or focused.
 */
const Carousel = forwardRef(
  (
    {
      autoplay = false,
      autoplaySpeed = 3000,
      dots = true,
      arrows = false,
      infinite = true,
      afterChange,
      beforeChange,
      className,
      style,
      children,
      ...rest
    },
    ref
  ) => {
    const { t } = useTranslation()
    const slides = Children.toArray(children).filter(Boolean)
    const [index, setIndex] = useState(0)
    const [paused, setPaused] = useState(false)

    const goTo = useCallback(
      (next) => {
        const total = slides.length
        if (total === 0) return
        const target = infinite ? (next + total) % total : Math.max(0, Math.min(next, total - 1))
        setIndex((previous) => {
          if (previous !== target) {
            beforeChange?.(previous, target)
            afterChange?.(target)
          }
          return target
        })
      },
      [afterChange, beforeChange, infinite, slides.length]
    )

    useImperativeHandle(ref, () => ({
      goTo,
      next: () => goTo(index + 1),
      prev: () => goTo(index - 1),
    }))

    useEffect(() => {
      if (!autoplay || paused || slides.length <= 1) return undefined
      const timer = setInterval(() => goTo(index + 1), autoplaySpeed)
      return () => clearInterval(timer)
    }, [autoplay, autoplaySpeed, goTo, index, paused, slides.length])

    return (
      <div
        className={cn('ui-carousel relative overflow-hidden', className)}
        style={style}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
        onBlur={() => setPaused(false)}
        aria-roledescription="carrousel"
        {...rest}
      >
        <div
          className="slick-track flex transition-transform duration-[var(--duration-slow)] ease-[var(--easing-standard)]" style={{ transform: `translateX(-${index * 100}%)` }}
        >
          {slides.map((slide, slideIndex) => (
            <div
              // eslint-disable-next-line react/no-array-index-key -- positional slides
              key={slideIndex}
              className="slick-slide w-full shrink-0" aria-hidden={slideIndex !== index}
            >
              {slide}
            </div>
          ))}
        </div>

        {arrows && slides.length > 1 ? (
          <>
            <button
              type="button" aria-label={t('common.previous')} onClick={() => goTo(index - 1)}
              className="slick-prev absolute left-2 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60" >
              <ChevronLeft size={18} aria-hidden="true" />
            </button>
            <button
              type="button" aria-label={t('common.next')} onClick={() => goTo(index + 1)}
              className="slick-next absolute right-2 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60" >
              <ChevronRight size={18} aria-hidden="true" />
            </button>
          </>
        ) : null}

        {dots && slides.length > 1 ? (
          <div className="slick-dots absolute bottom-2 left-1/2 flex -translate-x-1/2 gap-2">
            {slides.map((_, dotIndex) => (
              <button
                // eslint-disable-next-line react/no-array-index-key -- positional dots
                key={dotIndex}
                type="button" aria-label={`Aller à la diapositive ${dotIndex + 1}`}
                aria-current={dotIndex === index || undefined}
                onClick={() => goTo(dotIndex)}
                className={cn(
                  'h-2 rounded-full transition-all',
                  dotIndex === index ? 'w-5 bg-white' : 'w-2 bg-white/60'
                )}
              />
            ))}
          </div>
        ) : null}
      </div>
    )
  }
)

Carousel.displayName = 'Carousel'

export default Carousel
